// Set webpack nonce for CSP compliance before any imports
// This must be the very first code to execute
// Webpack will use this nonce when dynamically injecting script tags for code-split chunks
// The nonce value is set by the server in the template (bookingCoverme.hbs)
if (typeof window !== 'undefined' && window.__WEBPACK_NONCE__) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - __webpack_nonce__ is a webpack-specific global
  __webpack_nonce__ = window.__WEBPACK_NONCE__;
}

import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ReduxProvider } from '@trip-ux/components/providers';
import { MuxProvider } from '@manulife/mux';
import { BookingCovermeClient as BookingCoverme } from '@trip-ux/react-containers/booking-coverme';
import { SessionContainer } from '@trip-ux/react-containers/session-container';
import { environment } from './environments/environment';
import { createInitialStore, getLocaleRoutes, initializeNewRelic, updateConfigLang } from './utils';
import { COVERME_ORG } from '@trip-ux/constants';
import { configureCountries } from '@trip-ux/common';
import './mux-layer.css';

declare global {
  interface Window {
    APP_STATE: Record<string, any>;
    __WEBPACK_NONCE__?: string;
  }
  // Webpack nonce for CSP
  // NOSONAR - this must be an ambient `var` declaration so webpack's global
  // `__webpack_nonce__` is recognized at runtime. `let`/`const` are not
  // valid for ambient globals and would change the emitted globals semantics.
  var __webpack_nonce__: string;
}

export const renderToDom = (): void => {
  // Initialize New Relic BEFORE React renders
  initializeNewRelic();

  // R6 (inert data-island): when COVERME_R6_APPSTATE_INERT=on, the server emits
  // the SSR payload as `<script type="application/json" id="app-state">…</script>`
  // instead of `window.APP_STATE = {...}`. Browsers do NOT execute
  // `type=application/json` scripts, so V8 skips the 30-80 ms compile+eval tax
  // the executable form pays synchronously during HTML parsing. Read from the
  // inert tag if `window.APP_STATE` was not set by an executable block.
  // Fallback to the executable-block path preserves flag=off behavior.
  if (typeof window !== 'undefined' && !window.APP_STATE) {
    const el = document.getElementById('app-state');
    if (el?.textContent) {
      try {
        window.APP_STATE = JSON.parse(el.textContent);
      } catch (_e) {
        // Malformed payload — leave window.APP_STATE undefined; createInitialStore
        // will pass through undefined and Redux will boot with defaults.
      }
    }
  }

  const store = createInitialStore(window.APP_STATE, environment.session.reminderInSeconds);
  const localeRoutes = getLocaleRoutes(store);
  const basePath = localeRoutes.contextPath;
  configureCountries({ basePath });
  const isCoverme = store?.userProfile?.organizationCode === COVERME_ORG;
  const refCode = store?.userProfile?.whiteLabel?.referenceId;
  const baseName = `${localeRoutes.contextPath}${localeRoutes.routes.TRAVELLER_DETAILS}`;

  // Convert locale to MUX format (en -> en-CA, fr -> fr-CA)
  const muxLang = store.config.locale === 'fr' ? 'fr-CA' : 'en-CA';

  let homeUrl;
  if (!isCoverme) {
    homeUrl = `${baseName}?lang=${store.config.locale}&refCode=${refCode}`;
  } else {
    homeUrl = `${baseName}?lang=${store.config.locale}`;
  }
  //update config.locale manually in redux store so lang isn't rehydrated on locale change
  updateConfigLang(store.config.locale);
  const container = document.getElementById('react-coverme-container');
  if (container) {
    createRoot(container).render(
      <StrictMode>
          <BrowserRouter basename={basePath}>
                <MuxProvider langProvider={{ lang: muxLang }}>
                  <ReduxProvider
                    init={store}
                    persistConfig={{
                      ...environment.covermeBookingConfig,
                      key: environment.covermeBookingConfig.persistRoot,
                      whitelist: environment.covermeBookingConfig.whitelist
                    }}>
                    <SessionContainer countDownSeconds={environment.session.reminderInSeconds} homePage={homeUrl} />
                    <BookingCoverme/>
                  </ReduxProvider>
                </MuxProvider>
              </BrowserRouter>
      </StrictMode>
    );
  }
  setTimeout(() => {
    delete window.APP_STATE;
  }, 0);
};
renderToDom();
